/**
 * Copy Image: render the breakdown's print document to a PNG.
 *
 * The very same HTML the Print PDF path builds is loaded into a hidden frame at
 * the print page's width (10in = 960 CSS px) in "image" mode — one tall page,
 * no pagination — and screenshotted with html2canvas at 2× for crisp text.
 */
import html2canvas from "html2canvas";

import { buildBreakdownPrintHtml, mountHiddenFrame } from "./printDocument";

export const IMAGE_PAGE_WIDTH_PX = 960; // 10in at 96dpi, the letter-landscape page
export const IMAGE_SCALE = 2;

export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the image"))), "image/png");
      return;
    }
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const bytes = atob(dataUrl.split(",")[1]);
      const buf = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) buf[i] = bytes.charCodeAt(i);
      resolve(new Blob([buf], { type: "image/png" }));
    } catch (err) {
      reject(err);
    }
  });
}

/** Render `props` (the print document's props) to a PNG blob. */
export async function renderBreakdownImage(props) {
  const html = buildBreakdownPrintHtml(props, { mode: "image" });
  const { doc, iframe, cleanup } = await mountHiddenFrame(html, { width: IMAGE_PAGE_WIDTH_PX, height: 10 });
  try {
    const height = Math.max(
      doc.documentElement ? doc.documentElement.scrollHeight : 0,
      doc.body ? doc.body.scrollHeight : 0,
      1,
    );
    // Give the frame the page's full height so nothing is clipped by a viewport.
    iframe.style.height = `${height}px`;
    const canvas = await html2canvas(doc.body, {
      scale: IMAGE_SCALE,
      backgroundColor: "#ffffff",
      width: IMAGE_PAGE_WIDTH_PX,
      height,
      windowWidth: IMAGE_PAGE_WIDTH_PX,
      windowHeight: height,
      scrollX: 0,
      scrollY: 0,
      useCORS: true,
      logging: false,
    });
    return await canvasToPngBlob(canvas);
  } finally {
    cleanup();
  }
}
